import {
  InvitationCommandResponseV1Schema,
  MembershipCommandResponseV1Schema,
  OrganizationRosterResponseV1Schema,
  OwnershipCommandResponseV1Schema,
  type MembershipOperationV1,
  type OrganizationRosterResponseV1,
  type RosterEntryV1,
} from "@fulcrum5/suite-contracts";
import {
  Check,
  ChevronDown,
  CircleAlert,
  LoaderCircle,
  MailPlus,
  MoreHorizontal,
  RefreshCw,
  Shield,
  ShieldAlert,
  UserRoundPlus,
  Wrench,
  X,
} from "lucide-react";
import {
  FormEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";

type PageError = PlatformApiError | null;

interface TransferTarget {
  readonly membershipId: string;
  readonly name: string;
}

interface MenuAction {
  readonly id: string;
  readonly label: string;
  onSelect(opener: HTMLButtonElement | null): void;
}

function createIdempotencyKey(scope: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `${scope}-${uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`}`;
}

function describeError(error: unknown): PlatformApiError {
  if (error instanceof PlatformApiError) return error;
  return new PlatformApiError(0, {
    code: "network_unavailable",
    message: "Platform could not complete that request",
    requestId: "platform_web_client",
    retryable: true,
  });
}

function roleLabel(role: "owner" | "admin" | "member"): string {
  return role === "owner" ? "Owner" : role === "admin" ? "Admin" : "Member";
}

type InviteRole = "member" | "admin";

const INVITE_ROLE_OPTIONS: readonly {
  readonly value: InviteRole;
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
  readonly Icon: typeof Wrench;
}[] = [
  {
    value: "member",
    title: "Member",
    subtitle: "Build & collaborate",
    description:
      "Full access to the workspace — can view and edit the org, add agents, manage connections, and see all conversations. For teammates actively building the team.",
    Icon: Wrench,
  },
  {
    value: "admin",
    title: "Admin",
    subtitle: "Full control",
    description:
      "Everything a member can do, plus manage people, send invites, change roles, and delete teams. For people who run the workspace day-to-day.",
    Icon: Shield,
  },
];

function InviteRoleDropdown({
  value,
  onChange,
  disabled,
  allowAdmin,
}: {
  readonly value: InviteRole;
  onChange(next: InviteRole): void;
  readonly disabled: boolean;
  /** Owners may invite admins; org admins invite members only. */
  readonly allowAdmin: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const options = allowAdmin
    ? INVITE_ROLE_OPTIONS
    : INVITE_ROLE_OPTIONS.filter((option) => option.value === "member");
  const selected = options.find((option) => option.value === value) ?? INVITE_ROLE_OPTIONS[0]!;

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !triggerRef.current?.parentElement?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!allowAdmin && value === "admin") onChange("member");
  }, [allowAdmin, onChange, value]);

  return (
    <div className="invite-role-dd">
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Invite as ${selected.title}`}
        className="invite-role-dd__trigger"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        ref={triggerRef}
        type="button"
      >
        <selected.Icon aria-hidden="true" className="invite-role-dd__icon" size={16} strokeWidth={1.8} />
        <span className="invite-role-dd__trigger-label">{selected.title}</span>
        <ChevronDown aria-hidden="true" className="invite-role-dd__chevron" size={16} strokeWidth={1.8} />
      </button>
      {open ? (
        <div className="invite-role-dd__menu" id={menuId} role="listbox" aria-label="Invitation role">
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                aria-selected={isSelected}
                className={`invite-role-dd__item${isSelected ? " is-selected" : ""}`}
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                role="option"
                type="button"
              >
                <option.Icon aria-hidden="true" className="invite-role-dd__item-icon" size={18} strokeWidth={1.8} />
                <span className="invite-role-dd__item-copy">
                  <span className="invite-role-dd__item-title">
                    {option.title}
                    <span className="invite-role-dd__item-sub">{option.subtitle}</span>
                  </span>
                  <span className="invite-role-dd__item-desc">{option.description}</span>
                </span>
                {isSelected ? (
                  <Check aria-hidden="true" className="invite-role-dd__check" size={16} strokeWidth={2.2} />
                ) : (
                  <span className="invite-role-dd__check-spacer" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function statusLabel(status: string): string {
  return status.slice(0, 1).toUpperCase() + status.slice(1);
}

function rosterEntryKey(entry: RosterEntryV1): string {
  return entry.kind === "membership" ? `membership:${entry.member.id}` : `invitation:${entry.invitation.id}`;
}

function appendRosterPage(
  current: OrganizationRosterResponseV1 | null,
  page: OrganizationRosterResponseV1,
): OrganizationRosterResponseV1 {
  if (!current || current.organizationId !== page.organizationId) return page;
  const seen = new Set(current.entries.map(rosterEntryKey));
  return {
    ...page,
    entries: [...current.entries, ...page.entries.filter((entry) => !seen.has(rosterEntryKey(entry)))],
  };
}

function ErrorNotice({ error, onRetry }: { readonly error: PlatformApiError; readonly onRetry?: () => void }): JSX.Element {
  return (
    <div className="platform-notice platform-notice--error" role="alert">
      <CircleAlert aria-hidden="true" size={18} strokeWidth={1.8} />
      <div>
        <strong>We could not update this workspace.</strong>
        <p>{error.message}</p>
        <span>Request {error.requestId}</span>
      </div>
      {onRetry && error.retryable ? (
        <button className="platform-button platform-button--quiet" type="button" onClick={onRetry}>
          <RefreshCw aria-hidden="true" size={16} strokeWidth={1.8} />
          Retry
        </button>
      ) : null}
    </div>
  );
}

function MemberActionMenu({
  label,
  actions,
  disabled,
  busy = false,
}: {
  readonly label: string;
  readonly actions: readonly MenuAction[];
  readonly disabled: boolean;
  /** When true, replace the ⋯ control with a spinner (this row is processing). */
  readonly busy?: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !triggerRef.current?.parentElement?.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsidePress);
    return () => document.removeEventListener("mousedown", closeOnOutsidePress);
  }, [open]);

  useEffect(() => {
    if (busy) setOpen(false);
  }, [busy]);

  if (actions.length === 0 && !busy) return null;

  const blocked = disabled || busy;

  return (
    <div className="member-action-menu" data-busy={busy || undefined}>
      <button
        aria-busy={busy || undefined}
        aria-controls={open && !busy ? menuId : undefined}
        aria-expanded={busy ? false : open}
        aria-label={busy ? `Updating ${label}` : `Actions for ${label}`}
        className="platform-icon-button member-action-menu__trigger"
        disabled={blocked}
        onClick={() => {
          if (busy) return;
          setOpen((value) => !value);
        }}
        ref={triggerRef}
        title={busy ? `Updating ${label}` : `Actions for ${label}`}
        type="button"
      >
        {busy ? (
          <LoaderCircle aria-hidden="true" className="platform-spin" size={18} strokeWidth={1.8} />
        ) : (
          <MoreHorizontal aria-hidden="true" size={18} strokeWidth={1.8} />
        )}
      </button>
      {open && !busy ? (
        <div className="member-action-menu__items" id={menuId}>
          {actions.map((action) => (
            <button
              disabled={blocked}
              key={action.id}
              onClick={() => {
                setOpen(false);
                action.onSelect(triggerRef.current);
              }}
              type="button"
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TransferOwnershipDialog({
  target,
  confirmation,
  error,
  submitting,
  onConfirmationChange,
  onDismiss,
  onSubmit,
}: {
  readonly target: TransferTarget;
  readonly confirmation: string;
  readonly error: PageError;
  readonly submitting: boolean;
  onConfirmationChange(value: string): void;
  onDismiss(): void;
  onSubmit(): void;
}): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const first = dialog.querySelector<HTMLElement>("[data-dialog-initial-focus]");
    first?.focus();

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        event.preventDefault();
        onDismiss();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled])",
      )].filter((item) => item.offsetParent !== null);
      if (focusable.length === 0) return;
      const firstItem = focusable[0]!;
      const lastItem = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    dialog.addEventListener("keydown", onKeyDown);
    return () => dialog.removeEventListener("keydown", onKeyDown);
  }, [onDismiss, submitting]);

  const canSubmit = confirmation === "TRANSFER" && !submitting;
  return (
    <div className="platform-dialog-backdrop" role="presentation">
      <div aria-labelledby={titleId} aria-modal="true" className="platform-dialog" ref={dialogRef} role="dialog">
        <div className="platform-dialog__header">
          <div>
            <p className="platform-page__eyebrow">Irreversible access change</p>
            <h2 id={titleId}>Transfer ownership</h2>
          </div>
          <button
            aria-label="Close transfer ownership dialog"
            className="platform-icon-button"
            disabled={submitting}
            onClick={onDismiss}
            title="Close"
            type="button"
          >
            <X aria-hidden="true" size={18} strokeWidth={1.8} />
          </button>
        </div>
        <p className="platform-dialog__copy">
          {target.name} will become the organization owner. Your current owner access will be removed after the change completes.
        </p>
        <label className="platform-field" htmlFor="transfer-confirmation">
          <span>Type TRANSFER to confirm</span>
          <input
            autoComplete="off"
            data-dialog-initial-focus
            id="transfer-confirmation"
            onChange={(event) => onConfirmationChange(event.target.value)}
            value={confirmation}
          />
        </label>
        {error ? <ErrorNotice error={error} /> : null}
        <div className="platform-dialog__actions">
          <button className="platform-button platform-button--secondary" disabled={submitting} onClick={onDismiss} type="button">
            Cancel
          </button>
          <button className="platform-button platform-button--danger" disabled={!canSubmit} onClick={onSubmit} type="button">
            {submitting ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : null}
            Transfer ownership
          </button>
        </div>
      </div>
    </div>
  );
}

function sectionTitle(count: number, label: string): JSX.Element {
  return (
    <div className="member-section__heading">
      <h2>{label}</h2>
      <span>{count}</span>
    </div>
  );
}

export function MembersPage(): JSX.Element {
  const session = usePlatformSession();
  const organizationId = session.identity?.activeOrganizationId ?? null;
  const [roster, setRoster] = useState<OrganizationRosterResponseV1 | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<PageError>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<InviteRole>("member");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<TransferTarget | null>(null);
  const [transferConfirmation, setTransferConfirmation] = useState("");
  const [transferError, setTransferError] = useState<PageError>(null);
  const transferOpenerRef = useRef<HTMLButtonElement | null>(null);
  const activeOrganizationIdRef = useRef<string | null>(organizationId);
  const rosterRequestVersion = useRef(0);
  activeOrganizationIdRef.current = organizationId;

  const load = useCallback(async ({
    cursor = null,
    append = false,
    signal,
  }: {
    readonly cursor?: string | null;
    readonly append?: boolean;
    readonly signal?: AbortSignal;
  } = {}): Promise<boolean> => {
    const targetOrganizationId = organizationId;
    if (!targetOrganizationId) return false;
    const requestVersion = rosterRequestVersion.current + 1;
    rosterRequestVersion.current = requestVersion;
    setIsLoading(true);
    setError(null);
    try {
      const result = await session.api.request({
        path: cursor
          ? `/api/organizations/${targetOrganizationId}/members?cursor=${encodeURIComponent(cursor)}`
          : `/api/organizations/${targetOrganizationId}/members`,
        responseSchema: OrganizationRosterResponseV1Schema,
        signal,
      });
      if (
        signal?.aborted
        || rosterRequestVersion.current !== requestVersion
        || activeOrganizationIdRef.current !== targetOrganizationId
        || result.data.organizationId !== targetOrganizationId
      ) return false;
      setRoster((current) => append ? appendRosterPage(current, result.data) : result.data);
      return true;
    } catch (cause) {
      if (
        signal?.aborted
        || rosterRequestVersion.current !== requestVersion
        || activeOrganizationIdRef.current !== targetOrganizationId
      ) return false;
      setError(describeError(cause));
      return false;
    } finally {
      if (
        !signal?.aborted
        && rosterRequestVersion.current === requestVersion
        && activeOrganizationIdRef.current === targetOrganizationId
      ) setIsLoading(false);
    }
  }, [organizationId, session.api]);

  useEffect(() => {
    const controller = new AbortController();
    rosterRequestVersion.current += 1;
    setRoster(null);
    setError(null);
    setNotice(null);
    setBusyKey(null);
    setInviteEmail("");
    setInviteError(null);
    setTransferTarget(null);
    setTransferConfirmation("");
    setTransferError(null);
    if (!organizationId) {
      setIsLoading(false);
      return () => controller.abort();
    }
    void load({ signal: controller.signal });
    return () => controller.abort();
  }, [load, organizationId]);

  const entries = roster?.entries ?? [];
  const activeEntries = useMemo(() => entries.filter((entry) => (
    entry.kind === "membership" && entry.member.status === "active"
  )), [entries]);
  const pendingEntries = useMemo(() => entries.filter((entry) => (
    (entry.kind === "membership" && entry.member.status === "pending")
    || (entry.kind === "invitation" && entry.invitation.state === "pending")
  )), [entries]);
  const inactiveEntries = useMemo(() => entries.filter((entry) => (
    (entry.kind === "membership" && entry.member.status === "inactive")
    || (entry.kind === "invitation" && entry.invitation.state !== "pending")
  )), [entries]);
  const ownershipHealthy = roster?.ownershipHealth === "healthy";
  const mayInvite = roster?.capabilities.includes("invite_member") === true;
  const mayInviteAdmin = roster?.capabilities.includes("manage_admin") === true;

  const runMembershipAction = useCallback(async (entry: Extract<RosterEntryV1, { kind: "membership" }>, operation: MembershipOperationV1) => {
    setBusyKey(`membership:${entry.member.id}`);
    setError(null);
    setNotice(null);
    try {
      await session.api.request({
        path: `/api/memberships/${entry.member.id}`,
        method: "PATCH",
        body: { version: 1, operation },
        headers: { "idempotency-key": createIdempotencyKey(`membership-${operation}`) },
        responseSchema: MembershipCommandResponseV1Schema,
      });
      if (await load()) {
        setNotice(
          operation === "remove"
            ? `${entry.member.displayName} was removed from the organization.`
            : `${entry.member.displayName} was updated.`,
        );
      }
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusyKey(null);
    }
  }, [load, session.api]);

  const runInvitationAction = useCallback(async (
    entry: Extract<RosterEntryV1, { kind: "invitation" }>,
    operation: "resend" | "revoke",
  ) => {
    setBusyKey(`invitation:${entry.invitation.id}`);
    setError(null);
    setNotice(null);
    try {
      await session.api.request({
        path: operation === "resend"
          ? `/api/invitations/${entry.invitation.id}/resend`
          : `/api/invitations/${entry.invitation.id}`,
        method: operation === "resend" ? "POST" : "DELETE",
        body: operation === "resend" ? { version: 1 } : undefined,
        headers: { "idempotency-key": createIdempotencyKey(`invitation-${operation}`) },
        responseSchema: InvitationCommandResponseV1Schema,
      });
      if (await load()) setNotice(operation === "resend" ? "Invitation resent." : "Invitation revoked.");
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusyKey(null);
    }
  }, [load, session.api]);

  const inviteMember = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!organizationId || !mayInvite || !inviteEmail.trim()) return;
    const role: InviteRole = inviteRole === "admin" && mayInviteAdmin ? "admin" : "member";
    setBusyKey("invite");
    setError(null);
    setInviteError(null);
    setNotice(null);
    try {
      await session.api.request({
        path: `/api/organizations/${organizationId}/invitations`,
        method: "POST",
        body: { version: 1, email: inviteEmail.trim(), role },
        headers: { "idempotency-key": createIdempotencyKey("invite-member") },
        responseSchema: InvitationCommandResponseV1Schema,
      });
      if (activeOrganizationIdRef.current !== organizationId) return;
      setInviteEmail("");
      setInviteRole("member");
      if (await load()) {
        setNotice(role === "admin" ? "Admin invitation sent." : "Invitation sent.");
      }
    } catch (cause) {
      const nextError = describeError(cause);
      const fieldMessage = nextError.error.fieldErrors?.email;
      if (fieldMessage) setInviteError(fieldMessage);
      else setError(nextError);
    } finally {
      setBusyKey(null);
    }
  }, [inviteEmail, inviteRole, load, mayInvite, mayInviteAdmin, organizationId, session.api]);

  const dismissTransfer = useCallback(() => {
    setTransferTarget(null);
    setTransferConfirmation("");
    setTransferError(null);
    window.setTimeout(() => transferOpenerRef.current?.focus(), 0);
  }, []);

  const transferOwnership = useCallback(async () => {
    if (!organizationId || !transferTarget || transferConfirmation !== "TRANSFER") return;
    setBusyKey(`transfer:${transferTarget.membershipId}`);
    setTransferError(null);
    setNotice(null);
    try {
      await session.api.request({
        path: `/api/organizations/${organizationId}/transfer-ownership`,
        method: "POST",
        body: { version: 1, targetMembershipId: transferTarget.membershipId },
        headers: { "idempotency-key": createIdempotencyKey("transfer-ownership") },
        responseSchema: OwnershipCommandResponseV1Schema,
      });
      const refreshed = await load();
      if (!refreshed) return;
      dismissTransfer();
      setNotice("Ownership transferred. Your current access may now change.");
    } catch (cause) {
      setTransferError(describeError(cause));
    } finally {
      setBusyKey(null);
    }
  }, [dismissTransfer, load, organizationId, session.api, transferConfirmation, transferTarget]);

  const memberActions = (entry: Extract<RosterEntryV1, { kind: "membership" }>): readonly MenuAction[] => {
    const actions: MenuAction[] = entry.allowedOperations
      .filter((operation) => ownershipHealthy || (operation !== "promote_admin" && operation !== "demote_member"))
      .map((operation) => ({
        id: operation,
        label: operation === "deactivate" ? "Deactivate member"
          : operation === "reactivate" ? "Reactivate member"
            : operation === "remove" ? "Remove from organization"
              : operation === "promote_admin" ? "Promote to admin" : "Change to member",
        onSelect: () => { void runMembershipAction(entry, operation); },
      }));
    if (
      ownershipHealthy
      && roster?.capabilities.includes("transfer_ownership")
      && entry.member.status === "active"
      && entry.member.role !== "owner"
      && entry.member.userId !== session.identity?.userId
    ) {
      actions.push({
        id: "transfer-ownership",
        label: "Transfer ownership",
        onSelect: (opener) => {
          transferOpenerRef.current = opener;
          setTransferTarget({ membershipId: entry.member.id, name: entry.member.displayName });
          setTransferConfirmation("");
          setTransferError(null);
        },
      });
    }
    return actions;
  };

  const invitationActions = (entry: Extract<RosterEntryV1, { kind: "invitation" }>): readonly MenuAction[] => (
    entry.allowedOperations.map((operation) => ({
      id: operation,
      label: operation === "resend" ? "Resend invitation" : "Revoke invitation",
      onSelect: () => { void runInvitationAction(entry, operation); },
    }))
  );

  if (!organizationId) {
    return (
      <section className="platform-page members-page" aria-labelledby="members-title">
        <p className="platform-page__eyebrow">Organization access</p>
        <h1 id="members-title">Members</h1>
        <p className="platform-page__summary">Choose an organization before opening its member workspace.</p>
      </section>
    );
  }

  const renderEntry = (entry: RosterEntryV1): JSX.Element => {
    if (entry.kind === "membership") {
      const busy = busyKey === `membership:${entry.member.id}` || busyKey === `transfer:${entry.member.id}`;
      return (
        <li className="member-row" key={entry.member.id} data-busy={busy || undefined}>
          <div className="member-row__identity">
            <strong>{entry.member.displayName}</strong>
            <span>{entry.member.email ?? "No email available"}</span>
          </div>
          <span className="platform-badge" data-tone={entry.member.role}>{roleLabel(entry.member.role)}</span>
          <span className="platform-badge" data-tone={entry.member.status}>{statusLabel(entry.member.status)}</span>
          <div className="member-row__actions">
            <MemberActionMenu
              actions={memberActions(entry)}
              busy={busy}
              disabled={busyKey !== null}
              label={entry.member.displayName}
            />
          </div>
        </li>
      );
    }

    const busy = busyKey === `invitation:${entry.invitation.id}`;
    return (
      <li className="member-row" key={entry.invitation.id} data-busy={busy || undefined}>
        <div className="member-row__identity">
          <strong>{entry.invitation.email ?? "Invitation recipient unavailable"}</strong>
          <span>Invitation expires {new Date(entry.invitation.expiresAt).toLocaleDateString()}</span>
        </div>
        <span className="platform-badge" data-tone={entry.invitation.role}>{roleLabel(entry.invitation.role)}</span>
        <span className="platform-badge" data-tone={entry.invitation.state}>{statusLabel(entry.invitation.state)}</span>
        <div className="member-row__actions">
          <MemberActionMenu
            actions={invitationActions(entry)}
            busy={busy}
            disabled={busyKey !== null}
            label={entry.invitation.email ?? "invitation"}
          />
        </div>
      </li>
    );
  };

  return (
    <section className="platform-page members-page" aria-labelledby="members-title">
      <div className="platform-page__header">
        <div>
          <p className="platform-page__eyebrow">Organization management</p>
          <h1 id="members-title">Members</h1>
          <p className="platform-page__summary">Manage access using the permissions and roles confirmed by your organization.</p>
        </div>
        {mayInvite ? (
          <form className="member-invite" onSubmit={inviteMember}>
            <label className="platform-field" htmlFor="member-invite-email">
              <span className="platform-visually-hidden">Email address</span>
              <input
                aria-describedby={inviteError ? "member-invite-error" : undefined}
                aria-invalid={inviteError ? "true" : undefined}
                id="member-invite-email"
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="teammate@example.com"
                type="email"
                value={inviteEmail}
              />
            </label>
            <InviteRoleDropdown
              allowAdmin={mayInviteAdmin}
              disabled={busyKey !== null}
              onChange={setInviteRole}
              value={inviteRole}
            />
            <button className="platform-button" disabled={busyKey !== null || !inviteEmail.trim()} type="submit">
              {busyKey === "invite" ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <MailPlus aria-hidden="true" size={16} strokeWidth={1.8} />}
              Send
            </button>
            {inviteError ? <span className="platform-field__error" id="member-invite-error" role="alert">{inviteError}</span> : null}
          </form>
        ) : null}
      </div>

      {roster && !ownershipHealthy ? (
        <div className="platform-notice platform-notice--warning" role="status">
          <ShieldAlert aria-hidden="true" size={18} strokeWidth={1.8} />
          <div>
            <strong>Ownership needs staff recovery</strong>
            <p>Owner-gated commands are unavailable until the organization has one confirmed active owner.</p>
          </div>
        </div>
      ) : null}
      {notice ? (
        <div className="platform-notice platform-notice--success" role="status">
          <Check aria-hidden="true" size={18} strokeWidth={1.8} />
          <span>{notice}</span>
        </div>
      ) : null}
      {error ? <ErrorNotice error={error} onRetry={() => { void load(); }} /> : null}

      {isLoading && !roster ? (
        <div className="platform-loading" role="status"><LoaderCircle aria-hidden="true" className="platform-spin" size={20} /> Loading members</div>
      ) : null}
      {!isLoading && !error && roster && entries.length === 0 ? (
        <div className="platform-empty-state">
          <MailPlus aria-hidden="true" size={22} strokeWidth={1.7} />
          <p>No member records are available for this organization.</p>
        </div>
      ) : null}
      {roster ? (
        <div className="member-sections">
          <section className="member-section" aria-labelledby="active-members-title">
            <div id="active-members-title">{sectionTitle(activeEntries.length, "Active members")}</div>
            <ul className="member-list">{activeEntries.map(renderEntry)}</ul>
          </section>
          <section className="member-section" aria-labelledby="pending-access-title">
            <div id="pending-access-title">{sectionTitle(pendingEntries.length, "Pending access")}</div>
            <ul className="member-list">{pendingEntries.map(renderEntry)}</ul>
          </section>
          <section className="member-section" aria-labelledby="inactive-records-title">
            <div id="inactive-records-title">{sectionTitle(inactiveEntries.length, "Inactive records")}</div>
            <ul className="member-list">{inactiveEntries.map(renderEntry)}</ul>
          </section>
        </div>
      ) : null}
      {roster?.nextCursor ? (
        <button
          className="platform-button platform-button--secondary member-load-more"
          disabled={isLoading || busyKey !== null}
          onClick={() => { void load({ cursor: roster.nextCursor, append: true }); }}
          type="button"
        >
          {isLoading ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : null}
          Load more member records
        </button>
      ) : null}

      {transferTarget ? (
        <TransferOwnershipDialog
          confirmation={transferConfirmation}
          error={transferError}
          onConfirmationChange={setTransferConfirmation}
          onDismiss={dismissTransfer}
          onSubmit={() => { void transferOwnership(); }}
          submitting={busyKey === `transfer:${transferTarget.membershipId}`}
          target={transferTarget}
        />
      ) : null}
    </section>
  );
}
